import {BORRAR_EN_EXCEL, type ResultadoDelLote, type VistaPreviaDeCambios} from '@cnc/contracts';
import {useState} from 'react';
import {Link} from 'react-router';
import {useAplicarCambiosDeExportaciones, useCargas, useSubirCambiosDeExportaciones} from '../api/cargas';
import {ErrorDeApi} from '../api/cliente';
import {AYUDA_CARGA_DE_CAMBIOS as C} from '../componentes/ayuda-exportacion';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {PistaDeSoltar, ZonaParaSoltar} from '../componentes/zona-para-soltar';
import {ResultadoDeLote} from '../componentes/resultado-de-lote';
import {tr} from '../i18n/idioma';
import {cuantosCambios, todosLosCambios, type CambiosAceptados} from '../utilidades/cambios-aceptados';
import {fechaHora, numero, textoOGuion} from '../utilidades/formato';

// Cambios de exportaciones desde un Excel: el "Excel para editar" de la app
// o el archivo de seguimiento con la hoja Tracking Status. Se revisa que
// cambiaria y solo entonces se aplica.
export function PaginaCambiosExportaciones() {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [crearNuevas, setCrearNuevas] = useState(true);
  const [vista, setVista] = useState<VistaPreviaDeCambios | null>(null);
  // Cambios que se aceptan, uno por uno; al leer el archivo vienen todos marcados.
  const [aceptados, setAceptados] = useState<CambiosAceptados>(new Map());
  const [resultado, setResultado] = useState<ResultadoDelLote | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const subir = useSubirCambiosDeExportaciones();
  const aplicar = useAplicarCambiosDeExportaciones();
  const cargas = useCargas();

  const revisar = async () => {
    if (!archivo) return;
    setMensaje(null);
    setResultado(null);
    try {
      const leida = await subir.mutateAsync({archivo, crearNuevas});
      setVista(leida);
      setAceptados(todosLosCambios(leida.resultado));
    } catch (error) {
      setVista(null);
      setMensaje(
        error instanceof ErrorDeApi
          ? [error.message, ...(error.detalles ?? [])].join(' ')
          : tr('No se pudo leer el archivo.', 'Could not read the file.'),
      );
    }
  };

  // todo = "Aceptar todo"; si no, solo los cambios marcados.
  const confirmar = async (todo: boolean) => {
    if (!vista) return;
    setMensaje(null);
    try {
      setResultado(
        await aplicar.mutateAsync({
          id: vista.id,
          elegidos: todo
            ? undefined
            : [...aceptados.entries()].map(([codigo, campos]) => ({codigo, campos: [...campos]})),
        }),
      );
      setVista(null);
      setArchivo(null);
    } catch (error) {
      setMensaje(
        error instanceof ErrorDeApi
          ? error.message
          : tr('No se pudieron aplicar los cambios.', 'Could not apply the changes.'),
      );
    }
  };

  const deCambios = (cargas.data?.cargas ?? []).filter(c => c.tipo === 'tracking');
  const totalDeCambios = vista ? cuantosCambios(todosLosCambios(vista.resultado)) : 0;

  return (
    <section className="max-w-6xl">
      <Link to="/seguimiento" className="text-sm text-verde hover:underline">
        {tr('← Volver al seguimiento', '← Back to tracking')}
      </Link>
      <h1 className="mt-3 text-2xl font-semibold text-verde-oscuro">
        {tr('Cargar cambios desde Excel', 'Upload changes from Excel')}
      </h1>
      <p className="text-sm text-apagado">
        {tr(
          'Sirve el Excel "para editar" que descargas desde el seguimiento (con los filtros que tengas puestos) o el archivo de seguimiento de siempre con la hoja Tracking Status. Primero verás qué cambiaría en cada exportación; nada se guarda hasta que confirmes. Cada cambio queda en el historial de la exportación.',
          'Use the "Editable Excel" you download from tracking (with whatever filters you have set) or the usual tracking file with the Tracking Status sheet. First you will see what would change in each export; nothing is saved until you confirm. Every change is kept in the export history.',
        )}
      </p>

      <div className="mt-4 grid gap-3 rounded-lg border border-linea bg-white p-4 md:grid-cols-[2fr_1fr_auto]">
        <ZonaParaSoltar>
          <label className="block text-xs text-apagado">
            {tr('Archivo de Excel', 'Excel file')}
            <NotaDeAyuda titulo={tr('Archivo de Excel', 'Excel file')} texto={C.archivo} dentroDeEtiqueta />
            <input
              type="file"
              accept=".xlsx,.xls,.xlsm"
              onChange={e => {
                setArchivo(e.target.files?.[0] ?? null);
                setVista(null);
                setResultado(null);
              }}
              className="mt-1 block w-full text-sm file:mr-3 file:rounded-md file:border file:border-linea file:bg-papel file:px-3 file:py-1.5 file:text-sm"
            />
            {!archivo && <PistaDeSoltar />}
          </label>
        </ZonaParaSoltar>
        <label className="flex items-center gap-2 self-end pb-1.5 text-sm">
          <input type="checkbox" checked={crearNuevas} onChange={e => setCrearNuevas(e.target.checked)} />
          {tr('Crear las exportaciones que no existan', "Create exports that don't exist")}
          <NotaDeAyuda texto={C.crearNuevas} dentroDeEtiqueta />
        </label>
        <div className="flex items-end">
          <button
            type="button"
            onClick={() => void revisar()}
            disabled={!archivo || subir.isPending}
            className="rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
          >
            {subir.isPending ? tr('Leyendo…', 'Reading…') : tr('Revisar archivo', 'Review file')}
          </button>
        </div>
      </div>
      {mensaje && <p className="mt-3 text-sm text-alerta">{mensaje}</p>}

      {resultado && (
        <div className="mt-4 rounded-lg border border-verde/40 bg-verde-claro p-4 text-sm">
          <div className="font-medium text-verde-oscuro">{tr('Cambios aplicados', 'Changes applied')}</div>
          <ResultadoDeLote resultado={resultado} className="mt-2" />
        </div>
      )}

      {vista && (
        <div className="mt-4 space-y-3">
          <div className="rounded-lg border border-linea bg-white p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <div className="font-medium">{vista.formatoEtiqueta}</div>
                <div className="text-xs text-apagado">
                  {tr(
                    `${vista.archivo} · hojas: ${vista.hojas.join(', ') || '—'} · ${numero(vista.filas)} filas leídas`,
                    `${vista.archivo} · sheets: ${vista.hojas.join(', ') || '—'} · ${numero(vista.filas)} rows read`,
                  )}
                  {vista.crearNuevas
                    ? tr(' · crea las que no existan', " · creates those that don't exist")
                    : tr(' · no crea exportaciones nuevas', ' · does not create new exports')}
                </div>
              </div>
              {vista.aplicable ? (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void confirmar(false)}
                    disabled={aplicar.isPending || cuantosCambios(aceptados) === 0}
                    className="rounded-md border border-verde bg-white px-4 py-2 text-sm font-medium text-verde-oscuro hover:bg-verde-claro disabled:opacity-50"
                  >
                    {tr(
                      `Aplicar los marcados (${numero(cuantosCambios(aceptados))} de ${numero(totalDeCambios)})`,
                      `Apply the ticked ones (${numero(cuantosCambios(aceptados))} of ${numero(totalDeCambios)})`,
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => void confirmar(true)}
                    disabled={aplicar.isPending}
                    className="rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
                  >
                    {aplicar.isPending
                      ? tr('Aplicando…', 'Applying…')
                      : tr(
                          `✓ Aceptar todo (${numero(totalDeCambios)} cambios)`,
                          `✓ Accept all (${numero(totalDeCambios)} changes)`,
                        )}
                  </button>
                </div>
              ) : (
                <span className="text-sm text-apagado">{tr('Nada que aplicar', 'Nothing to apply')}</span>
              )}
            </div>
            {vista.errores.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm text-alerta">
                {vista.errores.map(e => (
                  <li key={e}>✕ {e}</li>
                ))}
              </ul>
            )}
            {vista.advertencias.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm text-tinta">
                {vista.advertencias.map(a => (
                  <li key={a}>! {a}</li>
                ))}
              </ul>
            )}
            {vista.resultado.resumen.conErrores > 0 && (
              <p className="mt-3 text-sm text-alerta">
                {tr(
                  `${numero(vista.resultado.resumen.conErrores)} exportaciones tienen errores: al aplicar se omiten y las demás sí se actualizan.`,
                  `${numero(vista.resultado.resumen.conErrores)} exports have errors: they are skipped when applying and the rest are updated.`,
                )}
              </p>
            )}
          </div>
          {vista.aplicable && (
            <p className="text-sm text-apagado">
              {tr(
                'Revisa cada cambio: desmarca los que no quieras aceptar (la casilla junto a la exportación marca o desmarca todos los suyos) y pulsa "Aplicar los marcados". "Aceptar todo" aplica todo lo que trae el archivo.',
                'Review each change: untick those you do not want to accept (the box next to the export ticks or unticks all of its changes) and click "Apply the ticked ones". "Accept all" applies everything in the file.',
              )}
              <button
                type="button"
                onClick={() => setAceptados(todosLosCambios(vista.resultado))}
                className="ml-2 text-verde hover:underline"
              >
                {tr('Marcar todos', 'Tick all')}
              </button>
              <button type="button" onClick={() => setAceptados(new Map())} className="ml-2 text-verde hover:underline">
                {tr('Desmarcar todos', 'Untick all')}
              </button>
            </p>
          )}
          <ResultadoDeLote
            resultado={vista.resultado}
            aceptados={vista.aplicable ? aceptados : undefined}
            onAceptados={vista.aplicable ? setAceptados : undefined}
          />
        </div>
      )}

      <div className="mt-6 rounded-lg border border-linea bg-white p-4 text-sm">
        <h2 className="font-medium">{tr('Cómo funciona el Excel para editar', 'How the Editable Excel works')}</h2>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-apagado">
          <li>
            {tr(
              'En Seguimiento pon los filtros que quieras y pulsa "Excel para editar": baja un archivo con esas exportaciones y todas las columnas editables.',
              'In Tracking, set the filters you want and click "Editable Excel": it downloads a file with those exports and all the editable columns.',
            )}
          </li>
          <li>
            {tr(
              'Cambia lo que necesites. Una celda vacía no cambia nada; para borrar un valor escribe',
              'Change what you need. An empty cell changes nothing; to clear a value type',
            )}{' '}
            {BORRAR_EN_EXCEL}.
          </li>
          <li>
            {tr(
              'Estado, Tipo y Financiada por BLQ tienen lista desplegable. Las fechas van como fecha de Excel o como 2026-10-15.',
              'Status, Type and Financed by BLQ have a drop-down list. Dates go as an Excel date or as 2026-10-15.',
            )}
          </li>
          <li>
            {tr(
              'Carga el archivo aquí, revisa qué cambiaría y confirma.',
              'Upload the file here, review what would change and confirm.',
            )}
          </li>
        </ol>
        <p className="mt-2 text-apagado">
          {tr(
            'Con la hoja Tracking Status las celdas vacías nunca borran; las facturas, pagos y observaciones no se cargan por aquí.',
            'With the Tracking Status sheet, empty cells never clear anything; invoices, payments and remarks are not uploaded here.',
          )}
        </p>
      </div>

      <div className="mt-6 rounded-lg border border-linea bg-white p-4">
        <h2 className="font-medium">{tr('Últimas cargas de cambios', 'Latest change uploads')}</h2>
        {cargas.data && deCambios.length === 0 && (
          <p className="mt-2 text-sm text-apagado">
            {tr('Todavía no se ha cargado ningún archivo de cambios.', 'No change file has been uploaded yet.')}
          </p>
        )}
        {deCambios.length > 0 && (
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                <th className="py-1 font-semibold">
                  {tr('Fecha', 'Date')}
                  <NotaDeAyuda titulo={tr('Fecha', 'Date')} texto={C.fecha} />
                </th>
                <th className="py-1 font-semibold">
                  {tr('Archivo', 'File')}
                  <NotaDeAyuda titulo={tr('Archivo', 'File')} texto={C.nombre} />
                </th>
                <th className="py-1 font-semibold">
                  {tr('Estado', 'Status')}
                  <NotaDeAyuda titulo={tr('Estado', 'Status')} texto={C.estado} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('Filas', 'Rows')}
                  <NotaDeAyuda titulo={tr('Filas', 'Rows')} texto={C.filas} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('Creadas', 'Created')}
                  <NotaDeAyuda titulo={tr('Creadas', 'Created')} texto={C.creadas} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('Actualizadas', 'Updated')}
                  <NotaDeAyuda titulo={tr('Actualizadas', 'Updated')} texto={C.actualizadas} />
                </th>
              </tr>
            </thead>
            <tbody>
              {deCambios.map(c => (
                <tr key={c.id} className="border-t border-linea/70">
                  <td className="py-1">{fechaHora(c.creadaEl)}</td>
                  <td className="max-w-64 truncate py-1" title={c.archivo ?? ''}>
                    {textoOGuion(c.archivo)}
                  </td>
                  <td className="py-1">
                    {c.estadoEtiqueta}
                    {c.error ? ` · ${c.error}` : ''}
                  </td>
                  <td className="py-1 text-right tabular-nums">{numero(c.filas)}</td>
                  <td className="py-1 text-right tabular-nums">{numero(c.nuevas)}</td>
                  <td className="py-1 text-right tabular-nums">{numero(c.actualizadas)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
